import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { TodoWithMeta, TodoStatus } from '../../types/todo.js';

const STATUS_OPTIONS: { key: TodoStatus; label: string }[] = [
  { key: 'todo', label: 'To Do' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'review', label: 'Review' },
  { key: 'done', label: 'Done' },
];

const PRIORITY_LABELS: Record<string, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
};

@customElement('cp-todo-card')
export class CpTodoCard extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Object }) todo!: TodoWithMeta;
  @state() private _expanded = false;

  private _isOverdue(): boolean {
    if (!this.todo.due_date) return false;
    return new Date(this.todo.due_date) < new Date();
  }

  private _formatDate(iso: string): string {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return iso;
    }
  }

  private _onEditClick(): void {
    this.dispatchEvent(new CustomEvent('todo-edit-request', {
      bubbles: true,
      composed: true,
      detail: { todo: this.todo },
    }));
  }

  private _onToggleExpand(): void {
    this._expanded = !this._expanded;
  }

  private _onStatusChange(e: Event): void {
    const select = e.target as HTMLSelectElement;
    const newStatus = select.value as TodoStatus;
    if (newStatus === this.todo.status) return;

    this.dispatchEvent(new CustomEvent('todo-status-change', {
      bubbles: true,
      composed: true,
      detail: { todoId: this.todo.id, status: newStatus },
    }));
  }

  render() {
    const { todo } = this;
    if (!todo) return nothing;

    const overdue = this._isOverdue();
    const hasSubTickets = todo.sub_ticket_total > 0;

    return html`
      <div class="cp-todo-card">
        <div class="cp-todo-card__header">
          <span class="cp-todo-card__title cp-todo-card__title--clickable" @click=${this._onEditClick}>${todo.title}</span>
          <span class="cp-todo-card__priority cp-todo-card__priority--${todo.priority}">
            ${PRIORITY_LABELS[todo.priority] ?? todo.priority}
          </span>
        </div>

        <div class="cp-todo-card__meta">
          ${todo.category_name ? html`
            <span
              class="cp-todo-card__category"
              style="--category-color: ${todo.category_color ?? 'var(--color-text-tertiary)'}"
            >${todo.category_name}</span>
          ` : nothing}

          ${todo.due_date ? html`
            <span class="cp-todo-card__deadline ${overdue ? 'cp-todo-card--overdue' : ''}">
              ${this._formatDate(todo.due_date)}
            </span>
          ` : nothing}

          ${hasSubTickets ? html`
            <button
              class="cp-todo-card__subtasks cp-todo-card__subtasks--clickable"
              @click=${this._onToggleExpand}
            >
              <span class="cp-todo-card__expand-icon ${this._expanded ? 'cp-todo-card__expand-icon--open' : ''}">&#9656;</span>
              ${todo.sub_ticket_done}/${todo.sub_ticket_total} Sub-Tickets
            </button>
          ` : html`
            <button
              class="cp-todo-card__subtasks cp-todo-card__subtasks--add"
              @click=${this._onToggleExpand}
            >+ Sub-Ticket</button>
          `}
        </div>

        <div class="cp-todo-card__actions">
          <select
            class="cp-todo-card__status-select"
            .value=${todo.status}
            @change=${this._onStatusChange}
          >
            ${STATUS_OPTIONS.map(opt => html`
              <option value=${opt.key} ?selected=${opt.key === todo.status}>${opt.label}</option>
            `)}
          </select>
        </div>

        ${this._expanded ? html`
          <div class="cp-todo-card__expand-area">
            <cp-todo-sub-tickets
              .parentTodoId=${todo.id}
              .pseudoProjectId=${todo.pseudo_project_id}
            ></cp-todo-sub-tickets>
          </div>
        ` : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-todo-card': CpTodoCard;
  }
}
